import { Box, Button, Flex, HStack, IconButton, Text } from "@chakra-ui/react";
import { MdCheck as CheckIcon, MdClose as CloseIcon } from "react-icons/md";
import Sheet from "@/components/Sheet";

interface TagGroup {
  name: string;
  tags: string[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tagGroups: TagGroup[];
  pendingTags: string[];
  onToggle: (tag: string) => void;
  onApply: () => void;
}

function TagChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: "5px",
        padding: "8px 14px", borderRadius: "9999px", fontSize: "13px",
        fontWeight: active ? 600 : 500,
        color: active ? "white" : "var(--chakra-colors-text-muted)",
        background: active ? "#40798C" : "transparent",
        border: `1px solid ${active ? "#40798C" : "var(--chakra-colors-border-muted)"}`,
        cursor: "pointer", transition: "all 0.15s",
      }}
    >
      {children}
    </button>
  );
}

export default function TagSheet({ isOpen, onClose, tagGroups, pendingTags, onToggle, onApply }: Props) {
  return (
    <Sheet isOpen={isOpen} onClose={onClose} zIndex={200}>
      <Box px={5} pb={2}>
        <HStack justify="space-between" mb={4}>
          <Text fontFamily="heading" fontWeight="800" fontSize="18px" color="text.primary">
            Add tags
          </Text>
          <IconButton
            aria-label="Close tag sheet"
            size="sm"
            variant="ghost"
            borderRadius="full"
            color="text.muted"
            _hover={{ bg: "bg.muted" }}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </HStack>

        <Box maxH="55vh" overflowY="auto" display="flex" flexDirection="column" gap={5} pb={4}>
          {tagGroups.map(group => (
            <Box key={group.name}>
              <Text fontSize="10px" fontWeight="600" textTransform="uppercase" letterSpacing="0.12em" color="text.muted" mb={3}>
                {group.name}
              </Text>
              <Flex flexWrap="wrap" gap={2}>
                {group.tags.map(tag => {
                  const active = pendingTags.includes(tag);
                  return (
                    <TagChip key={tag} active={active} onClick={() => onToggle(tag)}>
                      {active && <CheckIcon size={13} />}
                      {tag}
                    </TagChip>
                  );
                })}
              </Flex>
            </Box>
          ))}
        </Box>

        <Button
          w="100%"
          bg="accent.secondary"
          color="white"
          borderRadius="13px"
          fontFamily="heading"
          fontWeight="800"
          fontSize="15px"
          py="13px"
          mt={2}
          _hover={{ bg: "accent.secondaryHover" }}
          onClick={onApply}
        >
          Apply{pendingTags.length > 0 ? ` (${pendingTags.length})` : ""}
        </Button>
      </Box>
    </Sheet>
  );
}
